// frontend/src/widgets/WorksIntelligenceWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-03-11
// Updated:      2026-09-29 11:55 BST
//
// Description:  Works Intelligence widget.
//               - Location pinning via LocationEditModal.
//               - Reads/writes shared pinned location so Weather, Flood, Traffic and Works widgets stay in sync.
//               - Fetches /api/works-intelligence using settled pinned coordinates only.
//               - Preserves operational window override controls for testing/planning.
//               - Renders operational-impact-first Street Manager works summary.
//               - Provides authenticated Works Intelligence PDF preview/download using
//                 the same pinned location, radius, limit and effective lookup window.
//               - PDF preview is rendered in a body portal so it remains independent of
//                 widget/mobile host sizing and other dashboard stacking contexts.
//               - Intended for Monitor page and other live intelligence dashboards.
//
// Notes:
//               - This widget is location-first, not job-first.
//               - It does NOT use WorksIntelligenceCard because that card remains job-oriented.
//               - Location works intelligence requires a window_start and window_end; this widget
//                 supplies sensible defaults when the override controls are blank.
//
// Mobile Responsiveness (2026-09-10):
//               - Preserves /api/works-intelligence request parameters, shared-location
//                 synchronisation, override-window controls and Cache Bust semantics.
//               - Location, radius/limit and override-window controls stack cleanly on phones.
//               - Header/status/actions become touch-safe without changing click behaviour.
//               - Summary/Meta and Location/Top Risk panels stack naturally on mobile.
//               - Surfaced Disruptions uses the SAME disruption render tree and shows one
//                 complete disruption card per internal vertical scroll/snap.
//               - Dynamic mobile sizing uses the proven Vision pattern:
//                   restore previous host sizing;
//                   clear temporary disruption/root sizing;
//                   measure the natural first surfaced disruption;
//                   size the disruption viewport to one complete disruption;
//                   wait a second animation frame;
//                   resize the first width-matching fixed-height mobile host.
//               - ResizeObserver watches complete widget content plus the first surfaced
//                 disruption so live response/window changes cannot leave the widget clipped.
//               - No duplicate mobile data tree is mounted.
//
// Operational Notes:
//               - No backend route, request parameter, scoring, narrative or date-window
//                 behaviour is changed by this responsive pass.
//               - Existing 900px isNarrow behaviour is retained for current desktop/tablet
//                 presentation compatibility; mobile host logic applies below 768px.

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api from "../api/axiosInstance";
import { useTheme } from "../context/ThemeContext.jsx";
import LocationEditModal from "../components/LocationEditModal.jsx";

const SHARED_LOCATION_KEY = "jobsPinnedLocation";
const SHARED_LOCATION_EVENT = "jobs-pinned-location-changed";

const isFiniteNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n);
};

const fmtNum = (n, dp = 1) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  return x.toFixed(dp);
};

const safeText = (v, fallback = "—") => {
  if (v === null || v === undefined) return fallback;
  const s = String(v).trim();
  return s || fallback;
};

const formatApiError = (err) => {
  const status = err?.response?.status;
  const data = err?.response?.data;

  if (data == null) return status ? `${status} — Request failed` : err?.message || "Request failed";
  if (typeof data === "string") return status ? `${status} — ${data}` : data;

  const detail = data?.detail ?? data?.message ?? data;
  try {
    const text = typeof detail === "string" ? detail : JSON.stringify(detail);
    return status ? `${status} — ${text}` : text;
  } catch {
    return status ? `${status} — Error` : "Error";
  }
};

const formatDateTimeGB = (value) => {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/London",
    });
  } catch {
    return "—";
  }
};

const formatDistance = (km, m) => {
  if (isFiniteNumber(m) && Number(m) < 1000) return `${Math.round(Number(m))} m`;
  if (isFiniteNumber(km)) return `${Number(km).toFixed(3)} km`;
  return "—";
};

const formatDuration = (startAt, endAt) => {
  if (!startAt && !endAt) return "—";
  const start = startAt ? new Date(startAt) : null;
  const end = endAt ? new Date(endAt) : null;

  if (!start || Number.isNaN(start.getTime()) || !end || Number.isNaN(end.getTime())) {
    return "—";
  }

  const ms = end.getTime() - start.getTime();
  if (!Number.isFinite(ms) || ms < 0) return "—";

  const totalMinutes = Math.round(ms / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const mins = totalMinutes % 60;

  const parts = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (mins && days === 0) parts.push(`${mins}m`);

  return parts.length ? parts.join(" ") : "0m";
};

const toIsoNoMs = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return date.toISOString().replace(/\.\d{3}Z$/, "Z");
};

const getDefaultWindowStartIso = () => {
  const now = new Date();
  return toIsoNoMs(now);
};

const getDefaultWindowEndIso = () => {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return toIsoNoMs(d);
};

const riskToneMap = {
  low: {
    bg: "rgba(16, 185, 129, 0.10)",
    border: "rgba(16, 185, 129, 0.30)",
    text: "#047857",
    label: "Low",
  },
  moderate: {
    bg: "rgba(59, 130, 246, 0.10)",
    border: "rgba(59, 130, 246, 0.28)",
    text: "#1d4ed8",
    label: "Moderate",
  },
  high: {
    bg: "rgba(245, 158, 11, 0.12)",
    border: "rgba(245, 158, 11, 0.30)",
    text: "#b45309",
    label: "High",
  },
  severe: {
    bg: "rgba(239, 68, 68, 0.12)",
    border: "rgba(239, 68, 68, 0.35)",
    text: "#b91c1c",
    label: "Severe",
  },
  unknown: {
    bg: "rgba(107, 114, 128, 0.10)",
    border: "rgba(107, 114, 128, 0.25)",
    text: "#4b5563",
    label: "Unknown",
  },
};

const trafficManagementTone = (value) => {
  const t = String(value || "").trim().toLowerCase();

  if (t.includes("road_closure") || t.includes("closure")) {
    return {
      bg: "rgba(239, 68, 68, 0.10)",
      border: "rgba(239, 68, 68, 0.26)",
      text: "#b91c1c",
    };
  }

  if (t.includes("lane") || t.includes("multi_way_signals") || t.includes("signals")) {
    return {
      bg: "rgba(245, 158, 11, 0.10)",
      border: "rgba(245, 158, 11, 0.24)",
      text: "#b45309",
    };
  }

  return {
    bg: "rgba(59, 130, 246, 0.10)",
    border: "rgba(59, 130, 246, 0.24)",
    text: "#1d4ed8",
  };
};

const chipStyle = (tone) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "6px 12px",
  borderRadius: 9999,
  fontSize: 12,
  fontWeight: 900,
  background: tone.bg,
  border: `1px solid ${tone.border}`,
  color: tone.text,
});

const readSharedLocation = () => {
  try {
    const raw = localStorage.getItem(SHARED_LOCATION_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const lat = Number(parsed?.lat);
    const lon = Number(parsed?.lon);

    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;

    return {
      name: parsed?.name || "Pinned Location",
      lat,
      lon,
    };
  } catch {
    return null;
  }
};

const broadcastSharedLocation = (loc) => {
  window.dispatchEvent(
    new CustomEvent(SHARED_LOCATION_EVENT, {
      detail: {
        name: loc?.name || "Pinned Location",
        lat: Number(loc?.lat),
        lon: Number(loc?.lon),
        updated_at: new Date().toISOString(),
      },
    })
  );
};

const writeSharedLocation = (loc) => {
  const payload = {
    name: loc?.name || "Pinned Location",
    lat: Number(loc?.lat),
    lon: Number(loc?.lon),
    updated_at: new Date().toISOString(),
  };

  try {
    localStorage.setItem(SHARED_LOCATION_KEY, JSON.stringify(payload));
  } catch {
    // ignore
  }

  broadcastSharedLocation(payload);
};

const sameLocation = (a, b) =>
  Number(a?.lat) === Number(b?.lat) &&
  Number(a?.lon) === Number(b?.lon) &&
  String(a?.name || "") === String(b?.name || "");

const extractLocationFromAny = (obj) => {
  if (!obj || typeof obj !== "object") return null;

  const lat =
    obj?.lat ??
    obj?.latitude ??
    obj?.location?.lat ??
    obj?.location?.latitude ??
    null;

  const lon =
    obj?.lon ??
    obj?.lng ??
    obj?.longitude ??
    obj?.location?.lon ??
    obj?.location?.lng ??
    obj?.location?.longitude ??
    null;

  const name =
    obj?.name ??
    obj?.label ??
    obj?.title ??
    obj?.location?.name ??
    "Pinned Location";

  if (!isFiniteNumber(lat) || !isFiniteNumber(lon)) return null;

  return {
    name: String(name || "Pinned Location"),
    lat: Number(lat),
    lon: Number(lon),
  };
};

const resolveCreatedLocation = async (created) => {
  const direct = extractLocationFromAny(created);
  if (direct) return direct;

  const id = created?.id ?? created?.location_id ?? created?.location?.id ?? null;
  if (!id) return null;

  const r = await api.get(`/locations/${id}`);
  return extractLocationFromAny(r?.data);
};

const WorksSignalIcon = ({ riskBand = "unknown", size = 56 }) => {
  const tone = riskToneMap[String(riskBand || "").toLowerCase()] || riskToneMap.unknown;
  const fill = tone.text;

  if (String(riskBand || "").toLowerCase() === "severe") {
    return (
      <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
        <circle cx="32" cy="32" r="28" fill={tone.bg} stroke={tone.border} strokeWidth="2" />
        <path d="M32 14L50 46H14L32 14Z" fill={fill} />
        <rect x="30" y="25" width="4" height="11" rx="2" fill="#fff" />
        <circle cx="32" cy="40" r="2.2" fill="#fff" />
      </svg>
    );
  }

  if (String(riskBand || "").toLowerCase() === "high") {
    return (
      <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
        <circle cx="32" cy="32" r="28" fill={tone.bg} stroke={tone.border} strokeWidth="2" />
        <rect x="18" y="22" width="28" height="18" rx="4" fill={fill} />
        <rect x="23" y="18" width="18" height="6" rx="2" fill={fill} opacity="0.85" />
        <rect x="24" y="44" width="16" height="4" rx="2" fill={fill} opacity="0.85" />
      </svg>
    );
  }

  if (String(riskBand || "").toLowerCase() === "moderate") {
    return (
      <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
        <circle cx="32" cy="32" r="28" fill={tone.bg} stroke={tone.border} strokeWidth="2" />
        <path d="M18 38L26 30L31 34L43 24L46 27" fill="none" stroke={fill} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="18" y="42" width="28" height="4" rx="2" fill={fill} opacity="0.85" />
      </svg>
    );
  }

  if (String(riskBand || "").toLowerCase() === "low") {
    return (
      <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
        <circle cx="32" cy="32" r="28" fill={tone.bg} stroke={tone.border} strokeWidth="2" />
        <path d="M20 34L28 42L44 24" fill="none" stroke={fill} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
      <circle cx="32" cy="32" r="28" fill={tone.bg} stroke={tone.border} strokeWidth="2" />
      <circle cx="32" cy="24" r="3" fill={fill} />
      <circle cx="32" cy="32" r="3" fill={fill} />
      <circle cx="32" cy="40" r="3" fill={fill} />
    </svg>
  );
};

const DisruptionRow = ({ disruption, bodyFont, cardRef = null }) => {
  const tm = safeText(disruption?.traffic_management_type, "unknown");
  const tmTone = trafficManagementTone(tm);

  return (
    <div
      ref={cardRef}
      className="vx-works-disruption-card min-w-0"
      style={{
        borderRadius: 14,
        border: "1px solid rgba(0,0,0,0.08)",
        background: "#fff",
        padding: 12,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <div style={{ marginTop: 1 }}>
          <span style={chipStyle(tmTone)}>{tm.replace(/_/g, " ")}</span>
        </div>

        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 900,
              color: "rgba(0,0,0,0.82)",
              lineHeight: 1.25,
              fontFamily: bodyFont,
            }}
          >
            {safeText(disruption?.street_name, "Unnamed street")}
          </div>

          <div style={{ fontSize: 12, color: "rgba(0,0,0,0.64)", marginTop: 4, lineHeight: 1.45 }}>
            {safeText(disruption?.highway_authority, "Unknown authority")}
          </div>

          {disruption?.event_details ? (
            <div style={{ fontSize: 12, color: "rgba(0,0,0,0.70)", marginTop: 6, lineHeight: 1.45 }}>
              {safeText(disruption?.event_details)}
            </div>
          ) : null}

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
            <span
              style={chipStyle({
                bg: "rgba(17, 24, 39, 0.06)",
                border: "rgba(17, 24, 39, 0.12)",
                text: "#111827",
              })}
            >
              Distance: {formatDistance(disruption?.distance_km, disruption?.distance_m)}
            </span>

            <span
              style={chipStyle({
                bg: "rgba(99, 102, 241, 0.08)",
                border: "rgba(99, 102, 241, 0.22)",
                text: "#4338ca",
              })}
            >
              Severity: {isFiniteNumber(disruption?.severity_score) ? Number(disruption.severity_score) : 0}
            </span>

            <span
              style={chipStyle({
                bg: "rgba(245, 158, 11, 0.09)",
                border: "rgba(245, 158, 11, 0.22)",
                text: "#92400e",
              })}
            >
              Impact: {isFiniteNumber(disruption?.impact_score) ? Number(disruption.impact_score) : 0}
            </span>

            <span
              style={chipStyle({
                bg: "rgba(239, 68, 68, 0.08)",
                border: "rgba(239, 68, 68, 0.20)",
                text: "#b91c1c",
              })}
            >
              Relevance: {isFiniteNumber(disruption?.relevance_score) ? Number(disruption.relevance_score) : 0}
            </span>
          </div>

          <div style={{ fontSize: 11, color: "rgba(0,0,0,0.48)", marginTop: 8 }}>
            {formatDateTimeGB(disruption?.start_at)} → {formatDateTimeGB(disruption?.end_at)} · Duration:{" "}
            {formatDuration(disruption?.start_at, disruption?.end_at)}
          </div>
        </div>
      </div>
    </div>
  );
};

const WorksIntelligenceWidget = ({
  defaultRadiusM = 5000,
  defaultLimit = 20,
  allowWindowOverride = true,
}) => {
  const { theme } = useTheme();

  const widgetRootRef = useRef(null);
  const contentRef = useRef(null);
  const disruptionsViewportRef = useRef(null);
  const firstDisruptionRef = useRef(null);

  const [openPicker, setOpenPicker] = useState(false);
  const [isNarrow, setIsNarrow] = useState(() => window.innerWidth < 900);

  const [loc, setLoc] = useState(
    () =>
      readSharedLocation() || {
        name: "Pinned Location",
        lat: 51.5074,
        lon: -0.1278,
      }
  );

  const [radiusM, setRadiusM] = useState(defaultRadiusM);
  const [limit, setLimit] = useState(defaultLimit);
  const [windowStart, setWindowStart] = useState("");
  const [windowEnd, setWindowEnd] = useState("");

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [resp, setResp] = useState(null);

  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");

  const headerFont = useMemo(() => theme?.font_header || "var(--font-header, Arial)", [theme]);
  const bodyFont = useMemo(() => theme?.font_body || "var(--font-body, Arial)", [theme]);
  const accent = useMemo(() => theme?.accent_color || "var(--accent-color, #ff7a00)", [theme]);

  const hasCoords = isFiniteNumber(loc?.lat) && isFiniteNumber(loc?.lon);

  const btnBase = {
    border: "none",
    borderRadius: 999,
    padding: "10px 16px",
    fontWeight: 900,
    cursor: "pointer",
    fontFamily: bodyFont,
    transition: "transform 0.06s ease, filter 0.15s ease",
    userSelect: "none",
  };

  const mkBtnHandlers = (normal, hover, click) => ({
    onMouseEnter: (e) => {
      e.currentTarget.style.backgroundColor = hover || normal;
    },
    onMouseLeave: (e) => {
      e.currentTarget.style.backgroundColor = normal;
      e.currentTarget.style.transform = "translateY(0px)";
    },
    onMouseDown: (e) => {
      e.currentTarget.style.backgroundColor = click || hover || normal;
      e.currentTarget.style.transform = "translateY(1px)";
    },
    onMouseUp: (e) => {
      e.currentTarget.style.backgroundColor = hover || normal;
      e.currentTarget.style.transform = "translateY(0px)";
    },
  });

  const actionBtnStyle = useMemo(() => {
    const normal = theme?.button_action?.normal || "#1f7a8c";
    return { ...btnBase, backgroundColor: normal, color: "#fff" };
  }, [theme, bodyFont]);

  const generalBtnStyle = useMemo(() => {
    const normal = theme?.button_general?.normal || "#222";
    return { ...btnBase, backgroundColor: normal, color: "#fff" };
  }, [theme, bodyFont]);

  const actionHandlers = useMemo(
    () =>
      mkBtnHandlers(
        theme?.button_action?.normal || "#1f7a8c",
        theme?.button_action?.hover || "#256f7f",
        theme?.button_action?.click || "#1b5e6c"
      ),
    [theme]
  );

  const generalHandlers = useMemo(
    () =>
      mkBtnHandlers(
        theme?.button_general?.normal || "#222",
        theme?.button_general?.hover || "#333",
        theme?.button_general?.click || "#111"
      ),
    [theme]
  );

  const inputStyle = useMemo(
    () => ({
      height: 44,
      padding: "10px 14px",
      border: "1px solid rgba(0,0,0,0.12)",
      borderRadius: 16,
      width: "100%",
      background: "#fff",
      boxSizing: "border-box",
      fontFamily: bodyFont,
      fontSize: 14,
      outline: "none",
    }),
    [bodyFont]
  );

  const pinnedLabel = useMemo(() => {
    if (loc?.name && String(loc.name).trim()) return String(loc.name).trim();
    return "Pinned Location";
  }, [loc]);

  const effectiveWindowStart = useMemo(
    () => (windowStart ? new Date(windowStart).toISOString() : getDefaultWindowStartIso()),
    [windowStart]
  );

  const effectiveWindowEnd = useMemo(
    () => (windowEnd ? new Date(windowEnd).toISOString() : getDefaultWindowEndIso()),
    [windowEnd]
  );

  const fetchWorks = async ({ cacheBust = false } = {}) => {
    if (!hasCoords) {
      setResp(null);
      setError("Pinned location is invalid.");
      return;
    }

    setBusy(true);
    setError("");
    try {
      const r = await api.get("/works-intelligence", {
        params: {
          lat: Number(loc.lat),
          lon: Number(loc.lon),
          radius_m: radiusM,
          limit,
          window_start: effectiveWindowStart,
          window_end: effectiveWindowEnd,
          ...(cacheBust ? { cache_bust: 1 } : {}),
        },
      });
      setResp(r?.data || null);
    } catch (e) {
      setResp(null);
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const resetWindowOverride = () => {
    setWindowStart("");
    setWindowEnd("");
  };

  const revokePdfUrl = () => {
    setPdfUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return "";
    });
  };

  const fetchPdfPreview = async () => {
    if (!hasCoords) {
      setPdfError("Pinned location is invalid.");
      return;
    }

    setPdfBusy(true);
    setPdfError("");

    try {
      const r = await api.get("/works-intelligence/pdf/preview", {
        params: {
          lat: Number(loc.lat),
          lon: Number(loc.lon),
          radius_m: radiusM,
          limit,
          window_start: effectiveWindowStart,
          window_end: effectiveWindowEnd,
          location_name: pinnedLabel,
        },
        responseType: "blob",
      });

      const blob =
        r?.data instanceof Blob
          ? r.data
          : new Blob([r?.data], { type: "application/pdf" });

      if (!blob || blob.size === 0) {
        throw new Error("PDF preview did not return a PDF Blob.");
      }

      const nextUrl = URL.createObjectURL(blob);
      setPdfUrl((current) => {
        if (current) URL.revokeObjectURL(current);
        return nextUrl;
      });
    } catch (e) {
      revokePdfUrl();
      setPdfError(formatApiError(e));
    } finally {
      setPdfBusy(false);
    }
  };

  const openPdfPreview = () => {
    if (!hasCoords) return;
    setPdfOpen(true);
    setPdfError("");
  };

  const closePdfPreview = () => {
    setPdfOpen(false);
    setPdfError("");
    revokePdfUrl();
  };

  const openPdfInNewTab = () => {
    if (!pdfUrl) return;
    window.open(pdfUrl, "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    if (!pdfOpen || pdfUrl || pdfBusy) return;
    fetchPdfPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfOpen]);

  useEffect(
    () => () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    },
    [pdfUrl]
  );

  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 900);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const shared = readSharedLocation();
    if (shared) {
      setLoc((prev) => (sameLocation(prev, shared) ? prev : shared));
    }

    const onStorage = (event) => {
      if (event.key !== SHARED_LOCATION_KEY) return;
      const sharedLoc = readSharedLocation();
      if (!sharedLoc) return;
      setLoc((prev) => (sameLocation(prev, sharedLoc) ? prev : sharedLoc));
    };

    const onSharedLocation = (event) => {
      const nextLoc = event?.detail;
      if (!nextLoc || !isFiniteNumber(nextLoc?.lat) || !isFiniteNumber(nextLoc?.lon)) return;
      setLoc((prev) => (sameLocation(prev, nextLoc) ? prev : nextLoc));
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener(SHARED_LOCATION_EVENT, onSharedLocation);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(SHARED_LOCATION_EVENT, onSharedLocation);
    };
  }, []);

  const lastFetchKeyRef = useRef("");
  useEffect(() => {
    const key = [
      fmtNum(loc?.lat, 6),
      fmtNum(loc?.lon, 6),
      String(radiusM),
      String(limit),
      effectiveWindowStart,
      effectiveWindowEnd,
    ].join("|");

    if (key === lastFetchKeyRef.current) return;
    lastFetchKeyRef.current = key;

    if (hasCoords) {
      fetchWorks({ cacheBust: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc?.lat, loc?.lon, radiusM, limit, effectiveWindowStart, effectiveWindowEnd]);

  const liveBadge = useMemo(() => {
    const ok = !busy && !error && resp;
    const bg = ok ? "#e8f5e9" : error ? "#ffebee" : "#e3f2fd";
    const bd = ok ? "#c8e6c9" : error ? "#ffcdd2" : "#bbdefb";
    const fg = ok ? "#1b5e20" : error ? "#b71c1c" : "#0d47a1";
    const text = ok ? "Live" : error ? "Error" : busy ? "Loading" : "Idle";
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 12px",
          borderRadius: 999,
          fontSize: 12,
          fontWeight: 900,
          background: bg,
          border: `1px solid ${bd}`,
          color: fg,
        }}
      >
        <span style={{ width: 8, height: 8, borderRadius: 99, background: fg, display: "inline-block" }} />
        {text}
      </span>
    );
  }, [busy, error, resp]);

  const summary = resp?.summary || {};
  const topRisks = Array.isArray(resp?.top_risks) ? resp.top_risks : [];
  const disruptions = Array.isArray(resp?.disruptions) ? resp.disruptions : [];
  const riskBandKey = String(summary?.risk_band || "unknown").toLowerCase();
  const riskTone = riskToneMap[riskBandKey] || riskToneMap.unknown;

  useLayoutEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const root = widgetRootRef.current;
    const content = contentRef.current;
    const viewport = disruptionsViewportRef.current;
    const firstDisruption = firstDisruptionRef.current;

    if (!root || !content) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(max-width: 767px)");
    let animationFrame = 0;
    let hostRecord = null;

    const restoreHost = () => {
      if (!hostRecord?.node) return;

      hostRecord.node.style.height = hostRecord.height;
      hostRecord.node.style.minHeight = hostRecord.minHeight;
      hostRecord = null;
    };

    const findFixedMobileHost = () => {
      const rootRect = root.getBoundingClientRect();
      const candidates = [];

      let node = root.parentElement;
      let depth = 0;

      while (node && node !== document.body && depth < 6) {
        const rect = node.getBoundingClientRect();
        const inlineHeight = String(node.style?.height || "").trim();

        const widthMatches =
          Math.abs(rect.width - rootRect.width) < 90;

        const hasFixedInlineHeight =
          inlineHeight &&
          inlineHeight !== "auto" &&
          inlineHeight !== "100%" &&
          inlineHeight !== "100vh";

        if (widthMatches && hasFixedInlineHeight) {
          candidates.push(node);
        }

        node = node.parentElement;
        depth += 1;
      }

      return candidates.length ? candidates[0] : null;
    };

    const applyMobileSizing = () => {
      cancelAnimationFrame(animationFrame);
      restoreHost();

      if (viewport) {
        viewport.style.height = "";
        viewport.style.minHeight = "";
      }

      root.style.height = "";
      root.style.minHeight = "";
      content.style.height = "";
      content.style.minHeight = "";

      if (!mediaQuery.matches) {
        return;
      }

      animationFrame = requestAnimationFrame(() => {
        const currentFirstDisruption = firstDisruptionRef.current;

        if (viewport && currentFirstDisruption) {
          const cardHeight = Math.ceil(
            Math.max(
              currentFirstDisruption.getBoundingClientRect().height || 0,
              currentFirstDisruption.scrollHeight || 0
            )
          );

          if (Number.isFinite(cardHeight) && cardHeight > 0) {
            const viewportHeight = cardHeight + 20;
            viewport.style.height = `${viewportHeight}px`;
            viewport.style.minHeight = `${viewportHeight}px`;
          }
        }

        root.style.height = "auto";
        root.style.minHeight = "0px";
        content.style.height = "auto";
        content.style.minHeight = "0px";

        animationFrame = requestAnimationFrame(() => {
          const host = findFixedMobileHost();

          if (!host) {
            return;
          }

          hostRecord = {
            node: host,
            height: host.style.height,
            minHeight: host.style.minHeight,
          };

          const hostRect = host.getBoundingClientRect();
          const rootRect = root.getBoundingClientRect();
          const wrapperChromeHeight = Math.max(
            0,
            rootRect.top - hostRect.top
          );

          const requiredHostHeight = Math.ceil(
            Math.max(
              host.scrollHeight,
              wrapperChromeHeight + rootRect.height + 8
            )
          );

          if (
            !Number.isFinite(requiredHostHeight) ||
            requiredHostHeight <= 0
          ) {
            return;
          }

          host.style.height = `${requiredHostHeight}px`;
          host.style.minHeight = `${requiredHostHeight}px`;
        });
      });
    };

    applyMobileSizing();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => applyMobileSizing())
        : null;

    resizeObserver?.observe(content);

    if (resizeObserver && firstDisruption) {
      resizeObserver.observe(firstDisruption);
    }

    const handleMediaChange = () => applyMobileSizing();

    mediaQuery.addEventListener?.("change", handleMediaChange);
    window.addEventListener("resize", applyMobileSizing);

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();

      mediaQuery.removeEventListener?.("change", handleMediaChange);
      window.removeEventListener("resize", applyMobileSizing);

      restoreHost();

      if (viewport) {
        viewport.style.height = "";
        viewport.style.minHeight = "";
      }

      root.style.height = "";
      root.style.minHeight = "";
      content.style.height = "";
      content.style.minHeight = "";
    };
  }, [
    allowWindowOverride,
    busy,
    disruptions.length,
    effectiveWindowEnd,
    effectiveWindowStart,
    error,
    limit,
    loc?.lat,
    loc?.lon,
    openPicker,
    radiusM,
    resp,
    windowEnd,
    windowStart,
  ]);

  return (
    <div
      ref={widgetRootRef}
      className="h-auto min-h-0 w-full overflow-visible md:h-full"
      style={{
        border: "1px solid rgba(0,0,0,0.10)",
        borderRadius: 16,
        background: "#fff",
        padding: 16,
        fontFamily: bodyFont,
        boxSizing: "border-box",
        minWidth: 0,
      }}
    >
      <style>{`
        @media (max-width: 767px) {
          .vx-works-mobile-button {
            width: 100% !important;
            min-height: 44px;
            white-space: normal !important;
          }

          .vx-works-content input,
          .vx-works-content select {
            min-height: 44px;
          }

          .vx-works-disruptions-viewport {
            overflow-y: auto !important;
            overflow-x: hidden !important;
            overscroll-behavior-y: contain;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            scroll-snap-type: y mandatory;
          }

          .vx-works-disruptions-viewport::-webkit-scrollbar {
            display: none;
          }

          .vx-works-disruption-card {
            scroll-snap-align: start;
            scroll-snap-stop: always;
          }
        }
      `}</style>

      <div
        ref={contentRef}
        className="vx-works-content min-w-0"
      >
      <div
        style={{
          display: "flex",
          alignItems: isNarrow ? "stretch" : "flex-start",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          flexDirection: isNarrow ? "column" : "row",
        }}
      >
        <div>
          <div style={{ fontFamily: headerFont, fontSize: 22, fontWeight: 900, color: accent, lineHeight: 1.1 }}>
            Works Intelligence
          </div>
          <div style={{ fontSize: 13, color: "rgba(0,0,0,0.55)", marginTop: 4 }}>
            Street Manager works and restrictions intelligence for pinned location
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
            justifyContent: isNarrow ? "flex-start" : "flex-end",
          }}
        >
          {resp?.summary?.risk_band ? <span style={chipStyle(riskTone)}>{riskTone.label} Risk</span> : liveBadge}

          <button
            type="button"
            className="vx-works-mobile-button"
            style={generalBtnStyle}
            {...generalHandlers}
            onClick={openPdfPreview}
            disabled={!hasCoords || busy}
            title="Preview Works Intelligence PDF report"
          >
            PDF Report
          </button>

          <button
            type="button"
            className="vx-works-mobile-button"
            style={actionBtnStyle}
            {...actionHandlers}
            onClick={() => fetchWorks({ cacheBust: false })}
            disabled={!hasCoords || busy}
          >
            Refresh
          </button>
        </div>
      </div>

      <div style={{ height: 1, background: "rgba(0,0,0,0.08)", marginTop: 14, marginBottom: 14 }} />

      <div
        style={{
          borderRadius: 18,
          background: "rgba(0,0,0,0.03)",
          border: "1px solid rgba(0,0,0,0.06)",
          padding: 14,
        }}
      >
        <div
          className="grid grid-cols-1 gap-3 min-[900px]:grid-cols-[minmax(0,1fr)_160px_160px] min-[900px]:items-center"
        >
          <div>
            <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)", marginBottom: 6 }}>
              Pinned Location
            </div>
            <input style={inputStyle} value={pinnedLabel} readOnly disabled />
          </div>

          <div>
            <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)", marginBottom: 6 }}>
              Latitude
            </div>
            <input style={inputStyle} value={fmtNum(loc?.lat, 6)} readOnly disabled />
          </div>

          <div>
            <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)", marginBottom: 6 }}>
              Longitude
            </div>
            <input style={inputStyle} value={fmtNum(loc?.lon, 6)} readOnly disabled />
          </div>
        </div>

        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:items-end">
          <div>
            <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)", marginBottom: 6 }}>
              Radius (m)
            </div>
            <input
              style={inputStyle}
              type="number"
              min="100"
              max="50000"
              value={radiusM}
              onChange={(e) => {
                const raw = Number(e.target.value || defaultRadiusM);
                setRadiusM(Math.max(100, Math.min(50000, raw)));
              }}
            />
          </div>

          <div>
            <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)", marginBottom: 6 }}>
              Limit
            </div>
            <input
              style={inputStyle}
              type="number"
              min="1"
              max="100"
              value={limit}
              onChange={(e) => {
                const raw = Number(e.target.value || defaultLimit);
                setLimit(Math.max(1, Math.min(100, raw)));
              }}
            />
          </div>
        </div>

        <div className="mt-3 grid grid-cols-1 gap-2 sm:flex sm:flex-wrap sm:gap-2.5">
          <button
            type="button"
            className="vx-works-mobile-button"
            style={generalBtnStyle}
            {...generalHandlers}
            onClick={() => setOpenPicker(true)}
          >
            Pick Location (Map)
          </button>

          <button
            type="button"
            className="vx-works-mobile-button"
            style={actionBtnStyle}
            {...actionHandlers}
            onClick={() => fetchWorks({ cacheBust: true })}
            disabled={!hasCoords || busy}
          >
            Cache Bust
          </button>
        </div>

        {allowWindowOverride ? (
          <>
            <div style={{ height: 1, background: "rgba(0,0,0,0.08)", marginTop: 14, marginBottom: 14 }} />

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:items-end">
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)", marginBottom: 6 }}>
                  Override Window Start
                </div>
                <input
                  style={inputStyle}
                  type="datetime-local"
                  value={windowStart}
                  onChange={(e) => setWindowStart(e.target.value)}
                />
              </div>

              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)", marginBottom: 6 }}>
                  Override Window End
                </div>
                <input
                  style={inputStyle}
                  type="datetime-local"
                  value={windowEnd}
                  onChange={(e) => setWindowEnd(e.target.value)}
                />
              </div>
            </div>

            <div className="mt-2 break-words text-xs" style={{ color: "rgba(0,0,0,0.55)" }}>
              Effective window: {formatDateTimeGB(effectiveWindowStart)} → {formatDateTimeGB(effectiveWindowEnd)}
            </div>

            <div className="mt-3 grid grid-cols-1 gap-2 sm:flex sm:flex-wrap sm:gap-2.5">
              <button
                type="button"
                className="vx-works-mobile-button"
                style={generalBtnStyle}
                {...generalHandlers}
                onClick={resetWindowOverride}
              >
                Clear Override
              </button>

              <button
                type="button"
                className="vx-works-mobile-button"
                style={actionBtnStyle}
                {...actionHandlers}
                onClick={() => fetchWorks({ cacheBust: false })}
                disabled={!hasCoords || busy}
              >
                Apply Window
              </button>
            </div>
          </>
        ) : null}
      </div>

      <div
        style={{
          marginTop: 14,
          borderRadius: 22,
          border: "1px solid rgba(0,0,0,0.08)",
          background: "#fff",
          padding: 16,
          boxShadow: "0 8px 20px rgba(0,0,0,0.04)",
        }}
      >
        {error ? (
          <div style={{ color: "#b71c1c", fontWeight: 900, fontSize: 12 }}>{error}</div>
        ) : !resp ? (
          <div style={{ color: "rgba(0,0,0,0.55)", fontWeight: 900, fontSize: 12 }}>
            No works intelligence loaded yet.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-3 min-[900px]:grid-cols-[minmax(0,1fr)_280px] min-[900px]:items-stretch">
              <div
                style={{
                  borderRadius: 18,
                  background: "rgba(0,0,0,0.03)",
                  border: "1px solid rgba(0,0,0,0.06)",
                  padding: 14,
                }}
              >
                <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start">
                  <WorksSignalIcon riskBand={summary?.risk_band} size={60} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)" }}>Summary</div>
                    <div
                      style={{
                        fontSize: isNarrow ? 18 : 22,
                        fontWeight: 900,
                        color: "rgba(0,0,0,0.82)",
                        marginTop: 6,
                        lineHeight: 1.25,
                      }}
                    >
                      {safeText(resp?.narrative, "No works narrative returned")}
                    </div>

                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
                      <span style={chipStyle(riskTone)}>{riskTone.label} Risk</span>

                      <span
                        style={chipStyle({
                          bg: "rgba(17, 24, 39, 0.06)",
                          border: "rgba(17, 24, 39, 0.12)",
                          text: "#111827",
                        })}
                      >
                        Score: {isFiniteNumber(summary?.risk_score) ? Number(summary.risk_score) : 0}
                      </span>

                      <span
                        style={chipStyle({
                          bg: "rgba(99, 102, 241, 0.08)",
                          border: "rgba(99, 102, 241, 0.22)",
                          text: "#4338ca",
                        })}
                      >
                        Relevant works: {isFiniteNumber(summary?.count) ? Number(summary.count) : disruptions.length}
                      </span>
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: 14, display: "grid", gap: 6 }}>
                  <div style={{ fontSize: 13, color: "rgba(0,0,0,0.74)", lineHeight: 1.45 }}>
                    • Nearest disruption: {formatDistance(summary?.nearest_distance_km, summary?.nearest_distance_m)}
                  </div>
                  <div style={{ fontSize: 13, color: "rgba(0,0,0,0.74)", lineHeight: 1.45 }}>
                    • Strongest severity score: {isFiniteNumber(summary?.max_severity_score) ? Number(summary.max_severity_score) : 0}
                  </div>
                  <div style={{ fontSize: 13, color: "rgba(0,0,0,0.74)", lineHeight: 1.45 }}>
                    • Strongest impact score: {isFiniteNumber(summary?.max_impact_score) ? Number(summary.max_impact_score) : 0}
                  </div>
                </div>
              </div>

              <div
                style={{
                  borderRadius: 18,
                  background: "rgba(0,0,0,0.03)",
                  border: "1px solid rgba(0,0,0,0.06)",
                  padding: 14,
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)" }}>Meta</div>
                <div style={{ fontSize: 14, fontWeight: 900, color: "rgba(0,0,0,0.82)", marginTop: 4 }}>
                  {formatDateTimeGB(effectiveWindowStart)}
                </div>

                <div style={{ marginTop: 10, fontSize: 13, color: "rgba(0,0,0,0.70)" }}>
                  Window end: {formatDateTimeGB(effectiveWindowEnd)}
                </div>
                <div style={{ marginTop: 4, fontSize: 13, color: "rgba(0,0,0,0.70)" }}>
                  Source: {safeText(resp?.source, "streetmanager")}
                </div>
                <div style={{ marginTop: 4, fontSize: 13, color: "rgba(0,0,0,0.70)" }}>
                  Radius: {isFiniteNumber(resp?.query?.radius_m) ? `${Number(resp.query.radius_m)} m` : `${radiusM} m`}
                </div>
                <div style={{ marginTop: 4, fontSize: 13, color: "rgba(0,0,0,0.70)" }}>
                  Limit: {isFiniteNumber(resp?.query?.limit) ? Number(resp.query.limit) : limit}
                </div>
                <div style={{ marginTop: 4, fontSize: 13, color: "rgba(0,0,0,0.70)" }}>
                  Returned: {disruptions.length}
                </div>
              </div>
            </div>

            <div className="mt-3.5 grid grid-cols-1 gap-3 min-[900px]:grid-cols-2">
              <div
                style={{
                  borderRadius: 18,
                  background: "#fff",
                  border: "1px solid rgba(0,0,0,0.08)",
                  padding: 14,
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)" }}>Location</div>
                <div
                  className="break-words"
                  style={{ fontSize: 16, fontWeight: 900, color: "rgba(0,0,0,0.82)", marginTop: 6 }}
                >
                  {pinnedLabel}
                </div>

                <div style={{ marginTop: 10, fontSize: 13, color: "rgba(0,0,0,0.70)" }}>
                  Latitude: {fmtNum(resp?.location?.lat, 6)}
                </div>
                <div style={{ marginTop: 4, fontSize: 13, color: "rgba(0,0,0,0.70)" }}>
                  Longitude: {fmtNum(resp?.location?.lon, 6)}
                </div>
              </div>

              <div
                style={{
                  borderRadius: 18,
                  background: "#fff",
                  border: "1px solid rgba(0,0,0,0.08)",
                  padding: 14,
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)" }}>Top Risk</div>
                {topRisks.length ? (
                  <DisruptionRow disruption={topRisks[0]} bodyFont={bodyFont} />
                ) : (
                  <div style={{ fontSize: 13, color: "rgba(0,0,0,0.60)", marginTop: 6 }}>
                    No top risk identified.
                  </div>
                )}
              </div>
            </div>

            <div
              style={{
                marginTop: 14,
                borderRadius: 18,
                background: "#fff",
                border: "1px solid rgba(0,0,0,0.08)",
                padding: 14,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)" }}>
                  Surfaced Disruptions
                </div>
                <div style={{ fontSize: 12, color: "rgba(0,0,0,0.55)", fontWeight: 800 }}>
                  Showing {disruptions.length}
                </div>
              </div>

              <div ref={disruptionsViewportRef} className="vx-works-disruptions-viewport mt-2.5 grid h-auto gap-2.5 overflow-visible">
                {disruptions.length ? (
                  disruptions.map((disruption, index) => (
                    <DisruptionRow
                      key={disruption?.id || `${disruption?.street_name || "x"}-${disruption?.start_at || ""}`}
                      disruption={disruption}
                      bodyFont={bodyFont}
                      cardRef={index === 0 ? firstDisruptionRef : null}
                    />
                  ))
                ) : (
                  <div style={{ fontSize: 13, color: "rgba(0,0,0,0.60)" }}>
                    No relevant disruptions surfaced for this location and window.
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      </div>

      {pdfOpen && typeof document !== "undefined"
        ? createPortal(
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Works Intelligence PDF Preview"
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 152000,
                background: "rgba(15, 23, 42, 0.72)",
                display: "flex",
                alignItems: "stretch",
                justifyContent: "center",
                padding: isNarrow ? 0 : 20,
                boxSizing: "border-box",
              }}
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) closePdfPreview();
              }}
            >
              <div
                style={{
                  width: "100%",
                  maxWidth: 1280,
                  minWidth: 0,
                  minHeight: 0,
                  borderRadius: isNarrow ? 0 : 18,
                  background: "#fff",
                  boxShadow: "0 24px 70px rgba(0,0,0,0.34)",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  fontFamily: bodyFont,
                }}
              >
                <div
                  style={{
                    padding: isNarrow ? 14 : 16,
                    borderBottom: "1px solid rgba(0,0,0,0.10)",
                    display: "flex",
                    alignItems: isNarrow ? "stretch" : "center",
                    justifyContent: "space-between",
                    gap: 12,
                    flexDirection: isNarrow ? "column" : "row",
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontFamily: headerFont,
                        fontSize: isNarrow ? 18 : 21,
                        fontWeight: 900,
                        color: accent,
                        lineHeight: 1.15,
                      }}
                    >
                      Street Manager Works Report
                    </div>
                    <div
                      style={{
                        marginTop: 4,
                        fontSize: 12,
                        color: "rgba(0,0,0,0.58)",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {pinnedLabel} · {formatDateTimeGB(effectiveWindowStart)} →{" "}
                      {formatDateTimeGB(effectiveWindowEnd)}
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    <button
                      type="button"
                      className="vx-works-mobile-button"
                      style={actionBtnStyle}
                      {...actionHandlers}
                      onClick={fetchPdfPreview}
                      disabled={pdfBusy}
                    >
                      {pdfBusy ? "Generating…" : "Refresh Preview"}
                    </button>

                    <button
                      type="button"
                      className="vx-works-mobile-button"
                      style={generalBtnStyle}
                      {...generalHandlers}
                      onClick={openPdfInNewTab}
                      disabled={!pdfUrl || pdfBusy}
                    >
                      Open PDF
                    </button>

                    <button
                      type="button"
                      className="vx-works-mobile-button"
                      style={generalBtnStyle}
                      {...generalHandlers}
                      onClick={closePdfPreview}
                    >
                      Close
                    </button>
                  </div>
                </div>

                <div
                  style={{
                    flex: 1,
                    minHeight: 0,
                    background: "#e5e7eb",
                    position: "relative",
                  }}
                >
                  {pdfError ? (
                    <div
                      style={{
                        margin: 16,
                        padding: 14,
                        borderRadius: 12,
                        border: "1px solid #ffcdd2",
                        background: "#ffebee",
                        color: "#b71c1c",
                        fontSize: 13,
                        fontWeight: 800,
                      }}
                    >
                      {pdfError}
                    </div>
                  ) : pdfBusy && !pdfUrl ? (
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        display: "grid",
                        placeItems: "center",
                        color: "rgba(0,0,0,0.62)",
                        fontWeight: 900,
                        fontSize: 13,
                      }}
                    >
                      Generating PDF preview…
                    </div>
                  ) : pdfUrl ? (
                    <iframe
                      title="Works Intelligence PDF preview"
                      src={pdfUrl}
                      style={{
                        width: "100%",
                        height: "100%",
                        minHeight: isNarrow ? "calc(100vh - 190px)" : 680,
                        border: 0,
                        display: "block",
                        background: "#fff",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        display: "grid",
                        placeItems: "center",
                        color: "rgba(0,0,0,0.55)",
                        fontWeight: 800,
                        fontSize: 13,
                      }}
                    >
                      No PDF preview loaded.
                    </div>
                  )}
                </div>
              </div>
            </div>,
            document.body
          )
        : null}

      <LocationEditModal
        open={openPicker}
        onClose={() => setOpenPicker(false)}
        onCreated={async (created) => {
          try {
            const resolved = await resolveCreatedLocation(created);
            if (!resolved) {
              setOpenPicker(false);
              setError("Location was created, but coordinates could not be resolved from the response.");
              return;
            }
            setError("");
            setLoc(resolved);
            writeSharedLocation(resolved);
            setOpenPicker(false);
          } catch (e) {
            setOpenPicker(false);
            setError(formatApiError(e));
          }
        }}
        initial={{
          name: loc?.name,
          lat: loc?.lat,
          lon: loc?.lon,
          timezone: "Europe/London",
          country: "United Kingdom",
        }}
      />
    </div>
  );
};

export default WorksIntelligenceWidget;